import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Download } from 'lucide-react';
import {
  BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import {
  useReportMetrics,
  useOrdersByStage,
  useOrdersByCompany,
  useRevenueByMonth,
} from '../hooks/useReports.js';
import { useFeatureFlag } from '../lib/posthog.js';
import { useWorkspaceStore } from '../stores/workspace.js';

interface DateRange {
  from: string;
  to: string;
}

function getPresetRange(preset: string): DateRange {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  const fmt = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

  switch (preset) {
    case 'this_month': {
      const from = new Date(now.getFullYear(), now.getMonth(), 1);
      return { from: fmt(from), to: fmt(now) };
    }
    case 'last_30': {
      const from = new Date(now);
      from.setDate(now.getDate() - 30);
      return { from: fmt(from), to: fmt(now) };
    }
    case 'last_90': {
      const from = new Date(now);
      from.setDate(now.getDate() - 90);
      return { from: fmt(from), to: fmt(now) };
    }
    case 'this_year': {
      const from = new Date(now.getFullYear(), 0, 1);
      return { from: fmt(from), to: fmt(now) };
    }
    default:
      return { from: '', to: '' };
  }
}

function formatCurrency(value: number, currency: string = 'USD'): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency, maximumFractionDigits: 0 }).format(value);
}

function exportCsv(rows: Record<string, unknown>[], filename: string) {
  if (!rows.length) return;
  const headers = Object.keys(rows[0]!);
  const lines = [
    headers.join(','),
    ...rows.map((r) => headers.map((h) => JSON.stringify(r[h] ?? '')).join(',')),
  ];
  const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${filename}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function Reports() {
  const { t } = useTranslation('reports');
  const workspace = useWorkspaceStore((s) => s.workspace);
  const reportsEnabled = useFeatureFlag('reports');

  const [preset, setPreset] = useState('last_30');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');

  const range = useMemo<{ from?: string; to?: string }>(() => {
    const result: { from?: string; to?: string } = {};
    if (preset === 'custom') {
      if (customFrom) result.from = customFrom;
      if (customTo) result.to = customTo;
    } else {
      const p = getPresetRange(preset);
      if (p.from) result.from = p.from;
      if (p.to) result.to = p.to;
    }
    return result;
  }, [preset, customFrom, customTo]);

  const { data: metrics, isLoading: metricsLoading, isError: metricsError } = useReportMetrics(range);
  const { data: byStage = [], isLoading: stageLoading } = useOrdersByStage(range);
  const { data: byCompany = [], isLoading: companyLoading } = useOrdersByCompany(range);
  const { data: byMonth = [], isLoading: monthLoading } = useRevenueByMonth(range);

  const currency = workspace?.currency ?? 'USD';
  const orderLabel = workspace?.orderLabelPlural ?? 'Orders';

  if (!reportsEnabled) return null;

  return (
    <div className="page-enter" style={{ maxWidth: 1100, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 'var(--space-6)', flexWrap: 'wrap', gap: 'var(--space-4)' }}>
        <div>
          <h1 style={{ fontSize: 'var(--text-2xl)', fontWeight: 'var(--font-medium)', color: 'var(--color-text)', marginBottom: 'var(--space-1)' }}>
            {t('title')}
          </h1>
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
            {t('subtitle')}
          </p>
        </div>

        {/* Date range controls */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
          {/* Preset buttons */}
          {(['last_30', 'last_90', 'this_month', 'this_year'] as const).map((p) => (
            <button
              key={p} type="button"
              onClick={() => setPreset(p)}
              style={{
                padding: 'var(--space-1) var(--space-3)',
                fontSize: 'var(--text-xs)', fontWeight: preset === p ? 'var(--font-medium)' : undefined,
                color: preset === p ? 'var(--color-brand)' : 'var(--color-text-muted)',
                background: preset === p ? 'var(--color-brand-pale)' : 'none',
                border: `1px solid ${preset === p ? 'var(--color-brand)' : 'var(--color-border)'}`,
                borderRadius: 'var(--radius-full)',
                cursor: 'pointer', whiteSpace: 'nowrap',
              }}
            >
              {t(`date_range.presets.${p}`)}
            </button>
          ))}

          {/* Custom range */}
          <input
            type="date" value={customFrom} aria-label={t('date_range.from')}
            onChange={(e) => { setCustomFrom(e.target.value); setPreset('custom'); }}
            style={dateInputStyle}
          />
          <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>—</span>
          <input
            type="date" value={customTo} aria-label={t('date_range.to')}
            onChange={(e) => { setCustomTo(e.target.value); setPreset('custom'); }}
            style={dateInputStyle}
          />
        </div>
      </div>

      {/* Error */}
      {metricsError && (
        <div style={{ padding: 'var(--space-4)', borderRadius: 'var(--radius-md)', background: 'var(--color-danger-pale)', color: 'var(--color-danger)', fontSize: 'var(--text-sm)', marginBottom: 'var(--space-6)' }}>
          {t('load_error')}
        </div>
      )}

      {/* Metric cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 'var(--space-4)', marginBottom: 'var(--space-8)' }}>
        {([
          { key: 'total_orders', value: metricsLoading ? '—' : String(metrics?.totalOrders ?? 0) },
          { key: 'total_value', value: metricsLoading ? '—' : formatCurrency(metrics?.totalValue ?? 0, currency) },
          { key: 'open_orders', value: metricsLoading ? '—' : String(metrics?.openOrders ?? 0) },
          { key: 'due_this_week', value: metricsLoading ? '—' : String(metrics?.ordersDueThisWeek ?? 0) },
        ] as const).map(({ key, value }) => (
          <div key={key} style={{
            padding: 'var(--space-5)', border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-lg)', background: 'var(--color-bg)',
          }}>
            <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', fontWeight: 'var(--font-medium)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 'var(--space-2)' }}>
              {t(`metrics.${key}`)}
            </p>
            {metricsLoading ? (
              <div className="skeleton-line" style={{ height: 28, width: 80 }} />
            ) : (
              <p style={{ fontSize: 'var(--text-2xl)', fontWeight: 'var(--font-medium)', color: 'var(--color-text)' }}>
                {value}
              </p>
            )}
          </div>
        ))}
      </div>

      {/* Charts grid */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-8)' }}>

        {/* Revenue by month */}
        <ChartCard
          title={t('charts.revenue_by_month.title')}
          isLoading={monthLoading}
          isEmpty={!monthLoading && byMonth.length === 0}
          emptyMessage={t('empty.no_data')}
          onExport={() => exportCsv(byMonth as unknown as Record<string, unknown>[], `${t('export.filename_prefix')}-revenue-by-month`)}
          exportLabel={t('export.button')}
        >
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={byMonth} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: 'var(--color-text-muted)' }} />
              <YAxis yAxisId="revenue" orientation="left" tick={{ fontSize: 11, fill: 'var(--color-text-muted)' }} tickFormatter={(v: number) => formatCurrency(v, currency)} />
              <YAxis yAxisId="orders" orientation="right" tick={{ fontSize: 11, fill: 'var(--color-text-muted)' }} allowDecimals={false} />
              <Tooltip
                formatter={(value, name) =>
                  name === t('charts.revenue_by_month.line_label')
                    ? formatCurrency(Number(value), currency)
                    : value
                }
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line yAxisId="revenue" type="monotone" dataKey="revenue" name={t('charts.revenue_by_month.line_label')} stroke="var(--color-brand)" strokeWidth={2} dot={false} />
              <Bar yAxisId="orders" dataKey="orderCount" name={t('charts.revenue_by_month.bar_label')} fill="var(--color-brand-pale)" radius={[4, 4, 0, 0]} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* Orders by stage + Orders by company side by side */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-6)' }}>
          <ChartCard
            title={t('charts.orders_by_stage.title', { order_label_plural: orderLabel })}
            isLoading={stageLoading}
            isEmpty={!stageLoading && byStage.length === 0}
            emptyMessage={t('empty.no_data')}
            onExport={() => exportCsv(byStage as unknown as Record<string, unknown>[], `${t('export.filename_prefix')}-orders-by-stage`)}
            exportLabel={t('export.button')}
          >
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={byStage} layout="vertical" margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11, fill: 'var(--color-text-muted)' }} allowDecimals={false} />
                <YAxis type="category" dataKey="stageName" tick={{ fontSize: 11, fill: 'var(--color-text-muted)' }} width={90} />
                <Tooltip />
                <Bar dataKey="count" name={t('charts.orders_by_stage.bar_label')} fill="var(--color-brand)" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard
            title={t('charts.orders_by_company.title')}
            isLoading={companyLoading}
            isEmpty={!companyLoading && byCompany.length === 0}
            emptyMessage={t('empty.no_data')}
            onExport={() => exportCsv(byCompany as unknown as Record<string, unknown>[], `${t('export.filename_prefix')}-orders-by-company`)}
            exportLabel={t('export.button')}
          >
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={byCompany.slice(0, 10)} layout="vertical" margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11, fill: 'var(--color-text-muted)' }} />
                <YAxis type="category" dataKey="companyName" tick={{ fontSize: 11, fill: 'var(--color-text-muted)' }} width={100} />
                <Tooltip formatter={(v) => formatCurrency(Number(v), currency)} />
                <Bar dataKey="totalValue" name={t('charts.orders_by_company.value_label')} fill="var(--color-brand)" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>
      </div>
    </div>
  );
}

// ── ChartCard ─────────────────────────────────────────────────────────────────

function ChartCard({
  title, isLoading, isEmpty, emptyMessage, onExport, exportLabel, children,
}: {
  title: string;
  isLoading: boolean;
  isEmpty: boolean;
  emptyMessage: string;
  onExport: () => void;
  exportLabel: string;
  children: React.ReactNode;
}) {
  return (
    <div style={{
      border: '1px solid var(--color-border)', borderRadius: 'var(--radius-lg)',
      padding: 'var(--space-5)', background: 'var(--color-bg)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-4)' }}>
        <h2 style={{ fontSize: 'var(--text-base)', fontWeight: 'var(--font-medium)', color: 'var(--color-text)' }}>
          {title}
        </h2>
        <button
          type="button"
          onClick={onExport}
          disabled={isEmpty}
          style={{
            display: 'flex', alignItems: 'center', gap: 'var(--space-1)',
            padding: 'var(--space-1) var(--space-3)',
            background: 'none', border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-md)', fontSize: 'var(--text-xs)',
            color: isEmpty ? 'var(--color-text-muted)' : 'var(--color-text-secondary)',
            cursor: isEmpty ? 'not-allowed' : 'pointer',
          }}
        >
          <Download size={12} />
          {exportLabel}
        </button>
      </div>

      {isLoading ? (
        <div className="skeleton-block" style={{ height: 240, borderRadius: 'var(--radius-md)' }} />
      ) : isEmpty ? (
        <div style={{ height: 240, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>{emptyMessage}</p>
        </div>
      ) : children}
    </div>
  );
}

const dateInputStyle: React.CSSProperties = {
  padding: 'var(--space-1) var(--space-2)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--text-xs)',
  color: 'var(--color-text)',
  background: 'var(--color-bg)',
};
